import type { Session } from '@supabase/supabase-js';
import { z } from 'zod';

import { supabase } from '@/integrations/supabase/client';
import { supabaseRpcWithRetry } from '@/lib/networkRetry';
import { createLogger } from '@/lib/structuredLogger';
import type {
  CompanyPosition,
  CompanyRanking,
  DepartmentPosition,
  DepartmentRanking,
  LeaderboardData,
} from '@/types/leaderboard';

const logger = createLogger('LeaderboardData');
const labelSchema = z
  .string()
  .trim()
  .min(1)
  .max(160)
  .refine(
    (value) =>
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint !== undefined && codePoint >= 32 && codePoint !== 127;
      }),
    'Labels cannot contain control characters.',
  );
const pointsSchema = z.number().int().min(-1_000_000_000).max(1_000_000_000);
const rankSchema = z.number().int().positive().max(1_000_000);
const companyPositionSchema = z.strictObject({
  display_name: labelSchema,
  avg_points: pointsSchema,
  rank: rankSchema,
});
const departmentPositionSchema = z.strictObject({
  department: labelSchema,
  avg_points: pointsSchema,
  rank: rankSchema,
});
const leaderboardSchema = z
  .strictObject({
    status: z.enum(['available', 'no_membership']),
    minimum_cohort_size: z.literal(5),
    contributes: z.boolean(),
    viewer: z
      .strictObject({
        company_display_name: labelSchema,
        department: labelSchema.nullable(),
      })
      .nullable(),
    self_company_status: z.enum(['ranked', 'below_minimum']),
    self_department_status: z.enum(['ranked', 'below_minimum', 'not_set']),
    self_company: companyPositionSchema.nullable(),
    self_department: departmentPositionSchema.nullable(),
    department_rankings: z
      .array(departmentPositionSchema.extend({ is_self: z.boolean() }).strict())
      .max(10),
    company_rankings: z
      .array(companyPositionSchema.extend({ is_self: z.boolean() }).strict())
      .max(10),
  })
  .superRefine((leaderboard, context) => {
    if (leaderboard.status === 'no_membership') {
      const exposesMemberData =
        leaderboard.viewer !== null ||
        leaderboard.self_company !== null ||
        leaderboard.self_department !== null ||
        leaderboard.company_rankings.length > 0 ||
        leaderboard.department_rankings.length > 0 ||
        leaderboard.contributes ||
        leaderboard.self_company_status !== 'below_minimum' ||
        leaderboard.self_department_status !== 'not_set';
      if (exposesMemberData) {
        context.addIssue({ code: 'custom', message: 'A missing membership cannot expose rankings.' });
      }
      return;
    }

    if (!leaderboard.viewer) {
      context.addIssue({ code: 'custom', message: 'An available leaderboard needs a viewer.' });
      return;
    }

    if (
      (leaderboard.self_company_status === 'ranked') !==
      (leaderboard.self_company !== null)
    ) {
      context.addIssue({ code: 'custom', message: 'Company rank status and position must agree.' });
    }
    if (
      (leaderboard.self_department_status === 'ranked') !==
      (leaderboard.self_department !== null)
    ) {
      context.addIssue({ code: 'custom', message: 'Department rank status and position must agree.' });
    }
    if (
      (leaderboard.viewer.department === null) !==
      (leaderboard.self_department_status === 'not_set')
    ) {
      context.addIssue({ code: 'custom', message: 'Department status must reflect profile setup.' });
    }
    if (leaderboard.company_rankings.filter((ranking) => ranking.is_self).length > 1) {
      context.addIssue({ code: 'custom', message: 'Only one company row can represent the viewer.' });
    }
    if (leaderboard.department_rankings.filter((ranking) => ranking.is_self).length > 1) {
      context.addIssue({ code: 'custom', message: 'Only one department row can represent the viewer.' });
    }
    if (
      leaderboard.self_company &&
      leaderboard.self_company.display_name !== leaderboard.viewer.company_display_name
    ) {
      context.addIssue({ code: 'custom', message: 'The company position must belong to the viewer.' });
    }
    if (
      leaderboard.self_department &&
      leaderboard.self_department.department !== leaderboard.viewer.department
    ) {
      context.addIssue({ code: 'custom', message: 'The department position must belong to the viewer.' });
    }
    const companySelfRow = leaderboard.company_rankings.find((ranking) => ranking.is_self);
    if (
      companySelfRow &&
      (!leaderboard.self_company ||
        companySelfRow.display_name !== leaderboard.self_company.display_name ||
        companySelfRow.avg_points !== leaderboard.self_company.avg_points ||
        companySelfRow.rank !== leaderboard.self_company.rank)
    ) {
      context.addIssue({ code: 'custom', message: 'The selected company row must match the viewer.' });
    }
    const departmentSelfRow = leaderboard.department_rankings.find(
      (ranking) => ranking.is_self,
    );
    if (
      departmentSelfRow &&
      (!leaderboard.self_department ||
        departmentSelfRow.department !== leaderboard.self_department.department ||
        departmentSelfRow.avg_points !== leaderboard.self_department.avg_points ||
        departmentSelfRow.rank !== leaderboard.self_department.rank)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'The selected department row must match the viewer.',
      });
    }
  });

export type {
  CompanyPosition,
  CompanyRanking,
  DepartmentPosition,
  DepartmentRanking,
  LeaderboardData,
};

export function parseLeaderboardData(value: unknown): LeaderboardData {
  return leaderboardSchema.parse(value);
}

export async function fetchLeaderboardData(
  userId: string,
  session: Session,
): Promise<LeaderboardData> {
  if (!userId || session.user.id !== userId) {
    throw new Error('The leaderboard session does not match the requested member.');
  }

  const { data, error } = await supabaseRpcWithRetry<unknown>(
    supabase,
    'get_member_company_leaderboard',
  );

  if (error || !data) {
    logger.error('Company comparison request failed', new Error('Leaderboard RPC failed'));
    throw new Error('The company comparison could not be loaded.');
  }

  try {
    return parseLeaderboardData(data);
  } catch {
    logger.error(
      'Company comparison response was rejected',
      new Error('Invalid leaderboard payload'),
    );
    throw new Error('The company comparison could not be loaded.');
  }
}
